import React, { useEffect, useState } from "react";
import { Dropdown, Button, Select } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getEpisodes, getEpisode } from "../../apis/episode";
import { getPatient, getPatients } from "../../apis/patient";
import CommonPagination from "../../components/CommanPagination";
import { format, parseISO } from "date-fns";
import { calculateGestationalAge, formatGplad, parseGplad } from "../../utils/EpisodeMeta";

const formatDate = (iso: string | null | undefined) => {
  if (!iso) return null;
  try {
    return format(parseISO(iso), "dd MMM yyyy");
  } catch {
    return null;
  }
};

const EpisodeMetaSummary = ({ episode }: { episode: any }) => {
  const meta = episode.meta ?? {};
  const fields: any[] = episode.episodeType?.fields ?? [];

  const items = fields
    .filter((f: any) => {
      const v = meta[f.key];
      return v !== undefined && v !== null && v !== "";
    })
    .slice(0, 3)
    .map((f: any) => {
      const raw = meta[f.key];
      if (f.type === "lmp") {
        const ga = calculateGestationalAge(raw);
        return (
          <span key={f.key} className="flex items-center gap-1 whitespace-nowrap">
            <span className="text-gray-400">{f.label}:</span>
            <span>{raw}</span>
            {ga && (
              <span className="inline-flex items-center rounded-full bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700 ring-1 ring-inset ring-rose-100">
                {ga.label}
              </span>
            )}
          </span>
        );
      }
      if (f.type === "gplad") {
        const formatted = formatGplad(parseGplad(raw));
        if (!formatted) return null;
        return (
          <span key={f.key} className="flex items-center gap-1 whitespace-nowrap">
            <span className="text-gray-400">{f.label}:</span>
            <span className="font-mono text-xs">{formatted}</span>
          </span>
        );
      }
      return (
        <span key={f.key} className="flex items-center gap-1 whitespace-nowrap">
          <span className="text-gray-400">{f.label}:</span>
          <span>{typeof raw === "boolean" ? (raw ? "Yes" : "No") : String(raw)}</span>
        </span>
      );
    })
    .filter(Boolean);

  if (items.length === 0) return <span className="text-gray-400">—</span>;
  return <div className="flex flex-col gap-0.5 text-xs">{items}</div>;
};

const VisitTypeBadge = ({ type }: { type: string }) => {
  const isIpd = type?.toLowerCase() === "ipd";
  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
        isIpd
          ? "bg-purple-100 text-purple-700"
          : "bg-blue-100 text-blue-700"
      }`}
    >
      {isIpd ? "IPD" : "OPD"}
    </span>
  );
};

const EpisodeList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [episodes, setEpisodes] = useState<any[]>([]);
  const [meta, setMeta] = useState<any>();
  const [patientFilter, setPatientFilter] = useState<number | null>(null);
  const [patientOptions, setPatientOptions] = useState<any[]>([]);
  const [panelEpisode, setPanelEpisode] = useState<any>(null);
  const [panelVisits, setPanelVisits] = useState<any[]>([]);
  const [panelLoading, setPanelLoading] = useState(false);

  const fetchEpisodes = (params: any = {}) => {
    getEpisodes(params).then((res: any) => {
      setEpisodes(res.data?.data || res.data || []);
      setMeta(res.data?.meta || null);
    });
  };

  const searchPatients = (name: string) => {
    if (!name) { setPatientOptions([]); return; }
    getPatients({ name, per_page: 20 }).then((res: any) => {
      setPatientOptions(res.data?.data || res.data || []);
    });
  };

  useEffect(() => {
    const pid = searchParams.get("patient_id");
    const pidNum = pid ? parseInt(pid) : null;
    setPatientFilter(pidNum);
    fetchEpisodes({ per_page: 10, ...(pidNum ? { patient_id: pidNum } : {}) });
    if (pid) {
      getPatient(pid).then((res: any) => {
        const p = res.data?.data || res.data;
        if (p) setPatientOptions([p]);
      }).catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePatientFilter = (value: number | undefined) => {
    const pid = value ?? null;
    setPatientFilter(pid);
    fetchEpisodes({ per_page: 10, ...(pid ? { patient_id: pid } : {}) });
  };

  const onPaginationChange = (page: number, pageSize?: number) => {
    fetchEpisodes({ page, per_page: pageSize || 10, ...(patientFilter ? { patient_id: patientFilter } : {}) });
  };

  const openVisitsPanel = (episode: any) => {
    setPanelEpisode(episode);
    setPanelVisits([]);
    setPanelLoading(true);
    getEpisode(episode.id)
      .then((res: any) => {
        setPanelVisits(res.data?.data?.visits || []);
      })
      .finally(() => setPanelLoading(false));
  };

  const closePanel = () => {
    setPanelEpisode(null);
    setPanelVisits([]);
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">Episodes</h1>
          <p className="mt-2 text-sm text-gray-700">All patient episodes with types and status.</p>
        </div>
      </div>

      <div className="mt-4 mb-2">
        <Select
          showSearch
          allowClear
          placeholder="Filter by patient..."
          style={{ width: 280 }}
          filterOption={false}
          value={patientFilter ?? undefined}
          onSearch={searchPatients}
          onChange={handlePatientFilter}
          options={patientOptions.map((p: any) => ({
            value: p.id,
            label: [p.firstName, p.lastName].filter(Boolean).join(" ") || p.fullName || `Patient #${p.id}`,
          }))}
        />
      </div>

      <div className="flow-root mt-4">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                      Patient
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      Episode Type
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      Doctor
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      Details
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      Visits
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      Status
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      Created At
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {episodes.length > 0 ? (
                    episodes.map((episode) => {
                      const visitCount = episode.visitCount ?? episode.visits?.length ?? 0;
                      const latestDate = formatDate(episode.latestVisitDate);
                      return (
                        <tr key={episode.id}>
                          <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                            {episode.patient ? `${episode.patient.fullName}`.trim() : "-"}
                          </td>
                          <td className="py-4 px-3 text-sm text-gray-500">
                            {episode.episodeType?.name || "-"}
                          </td>
                          <td className="py-4 px-3 text-sm text-gray-500">
                            {episode.assignedUser
                              ? [episode.assignedUser.firstName, episode.assignedUser.lastName].filter(Boolean).join(" ") || episode.assignedUser.name || "-"
                              : "-"}
                          </td>
                          <td className="py-4 px-3 text-sm text-gray-700">
                            <EpisodeMetaSummary episode={episode} />
                          </td>
                          <td className="py-4 px-3 text-sm">
                            <button
                              onClick={() => openVisitsPanel(episode)}
                              className="group text-left"
                            >
                              <span className="font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors">
                                {visitCount}
                              </span>
                              {latestDate ? (
                                <span className="block text-xs text-gray-400 mt-0.5">
                                  Last: {latestDate}
                                </span>
                              ) : visitCount === 0 ? (
                                <span className="block text-xs text-gray-400 mt-0.5">No visits yet</span>
                              ) : null}
                            </button>
                          </td>
                          <td className="py-4 px-3 text-sm text-gray-500 capitalize">
                            {episode.status || "-"}
                          </td>
                          <td className="py-4 px-3 text-sm text-gray-500">
                            {episode.createdAt ? formatDate(episode.createdAt) : "-"}
                          </td>
                          <td className="py-4 px-3 text-sm text-gray-500">
                            <Dropdown
                              menu={{
                                items: [
                                  {
                                    key: "create-visit",
                                    label: (
                                      <span
                                        onClick={() => {
                                          const patientId = episode.patientId || episode.patient?.id;
                                          if (patientId) {
                                            navigate(`/patients/${patientId}/visits/types?episodeId=${episode.id}`);
                                          }
                                        }}
                                      >
                                        Create Visit
                                      </span>
                                    ),
                                  },
                                  {
                                    key: "view-visits",
                                    label: (
                                      <span onClick={() => openVisitsPanel(episode)}>
                                        View Visits
                                      </span>
                                    ),
                                  },
                                ],
                              }}
                              placement="bottomLeft"
                            >
                              <Button>Actions</Button>
                            </Dropdown>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={7} className="py-4 pr-3 pl-4 text-sm font-medium text-gray-900 sm:pl-6">
                        Record Not Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <CommonPagination meta={meta} onChange={onPaginationChange} />
          </div>
        </div>
      </div>

      {/* Visits slide-over panel */}
      {panelEpisode && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-30 z-40 transition-opacity"
            onClick={closePanel}
          />

          {/* Panel */}
          <div className="fixed inset-y-0 right-0 z-50 w-full max-w-md bg-white shadow-xl flex flex-col">
            {/* Header */}
            <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-base font-semibold text-gray-900">
                  {panelEpisode.patient?.fullName || "Patient"}'s Visits
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  {panelEpisode.episodeType?.name || "Episode"} &middot; {panelVisits.length} visit{panelVisits.length !== 1 ? "s" : ""}
                </p>
              </div>
              <button
                onClick={closePanel}
                className="text-gray-400 hover:text-gray-600 transition-colors ml-4"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Episode meta details */}
            {panelEpisode && (() => {
              const meta = panelEpisode.meta ?? {};
              const fields: any[] = panelEpisode.episodeType?.fields ?? [];
              const populated = fields.filter((f: any) => {
                const v = meta[f.key];
                return v !== undefined && v !== null && v !== "";
              });
              if (populated.length === 0) return null;
              return (
                <div className="px-5 py-3 bg-indigo-50 border-b border-indigo-100">
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
                    {populated.map((f: any) => {
                      const raw = meta[f.key];
                      let display: React.ReactNode = String(raw);
                      if (f.type === "lmp") {
                        const ga = calculateGestationalAge(raw);
                        display = (
                          <span className="flex items-center gap-1.5">
                            <span>{raw}</span>
                            {ga && (
                              <span className="inline-flex items-center rounded-full bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700 ring-1 ring-inset ring-rose-100">
                                {ga.label} GA
                              </span>
                            )}
                          </span>
                        );
                      } else if (f.type === "gplad") {
                        display = <span className="font-mono text-xs">{formatGplad(parseGplad(raw))}</span>;
                      } else if (f.type === "boolean") {
                        display = <span className={raw ? "text-green-700" : "text-gray-500"}>{raw ? "Yes" : "No"}</span>;
                      }
                      return (
                        <div key={f.key}>
                          <dt className="text-[10px] font-semibold uppercase tracking-wider text-indigo-400">{f.label}</dt>
                          <dd className="mt-0.5 text-sm text-gray-800">{display}</dd>
                        </div>
                      );
                    })}
                  </dl>
                </div>
              );
            })()}

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {panelLoading ? (
                <div className="flex items-center justify-center h-32 text-sm text-gray-400">
                  Loading visits...
                </div>
              ) : panelVisits.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-center">
                  <p className="text-sm font-medium text-gray-500">No visits yet</p>
                  <p className="text-xs text-gray-400 mt-1">Create the first visit for this episode</p>
                </div>
              ) : (
                <ul className="space-y-2">
                  {panelVisits.map((visit: any, idx: number) => (
                    <li
                      key={visit.id}
                      className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3 hover:border-indigo-200 hover:bg-indigo-50 transition-colors cursor-pointer group"
                      onClick={() =>
                        navigate(
                          visit.visitType === "ipd"
                            ? `/ipd/visits/${visit.id}`
                            : `/opd/visits/${visit.id}`
                        )
                      }
                    >
                      <div className="flex items-center gap-3">
                        <span className="flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 text-xs font-semibold text-gray-500 group-hover:bg-white">
                          {idx + 1}
                        </span>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">
                              {visit.serialNumber || `Visit #${visit.id}`}
                            </span>
                            <VisitTypeBadge type={visit.visitType} />
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            {visit.date && (
                              <span className="text-xs text-gray-400">
                                {formatDate(visit.date)}
                              </span>
                            )}
                            {visit.visitTypeName && (
                              <span className="text-xs text-gray-400">&middot; {visit.visitTypeName}</span>
                            )}
                            {visit.status && (
                              <span className="text-xs text-gray-400">&middot; {visit.status}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-gray-300 group-hover:text-indigo-400 transition-colors" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                      </svg>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 py-3 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  closePanel();
                  const patientId = panelEpisode.patientId || panelEpisode.patient?.id;
                  if (patientId) {
                    navigate(`/patients/${patientId}/visits/types?episodeId=${panelEpisode.id}`);
                  }
                }}
                className="w-full py-2 text-sm font-medium text-indigo-600 bg-white border border-indigo-300 rounded-md hover:bg-indigo-50 transition-colors"
              >
                + Create New Visit
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EpisodeList;
